import { useState, useEffect } from 'react';
import { Box, MenuItem, useMediaQuery, useTheme } from '@mui/material';

import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  CustomAutoComplete,
  HorizontalForm,
  VerticalForm,
  CustomTextArea,
} from '../../../common/components';
import BeneficiarySelectWithCreate from '../../common/components/BeneficiarySelectWithCreate';

import { useCreateBankMovement, useUpdateBankMovement } from '../../../hooks/useBankMovements';
import { useMovementTypes } from '../../../hooks/useMovementTypes';
import { useMovementConcepts } from '../../../hooks/useMovementConcepts';
import { useLegalEntities } from '../../../hooks/useLegalEntities';
import { formatAccountType, formatBankName } from '../utils/formatters';

const VALIDATION_START_DATE = '2025-11-08';

const MovementDialog = ({ open, onClose, movement = null, account, editMode = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    uuid_movement_type: '',
    uuid_movement_concept: '',
    emission_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    amount: '',
    concept: '',
    voucher_number: '',
    uuid_legal_entity: '',
    is_reconciled: false,
    observations: '',
  });

  const [errors, setErrors] = useState({});

  const { data: legalEntities = [] } = useLegalEntities();
  const { data: movementTypes = [] } = useMovementTypes();
  const { data: movementConcepts = [] } = useMovementConcepts();

  const createMovementMutation = useCreateBankMovement();
  const updateMovementMutation = useUpdateBankMovement();

  useEffect(() => {
    if (open) {
      if (editMode && movement) {
        setFormData({
          uuid_movement_type: movement.uuid_movement_type || '',
          uuid_movement_concept: movement.uuid_movement_concept || '',
          emission_date: movement.emission_date
            ? new Date(movement.emission_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          due_date: movement.due_date
            ? new Date(movement.due_date).toISOString().split('T')[0]
            : movement.emission_date
            ? new Date(movement.emission_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          amount: movement.amount?.toString() || '',
          concept: movement.concept || '',
          voucher_number: movement.voucher_number || '',
          uuid_legal_entity: movement.uuid_legal_entity || '',
          is_reconciled: movement.is_reconciled || false,
          observations: movement.observations || '',
        });
      } else {
        setFormData({
          uuid_movement_type: '',
          uuid_movement_concept: '',
          emission_date: new Date().toISOString().split('T')[0],
          due_date: new Date().toISOString().split('T')[0],
          amount: '',
          concept: '',
          voucher_number: '',
          uuid_legal_entity: '',
          is_reconciled: false,
          observations: '',
        });
      }
      setErrors({});
    }
  }, [open, editMode, movement]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Si se está cambiando el concepto de movimiento, actualizar también el campo concept
    if (name === 'uuid_movement_concept') {
      const selectedConcept = movementConcepts.find((c) => c.uuid === value);
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        concept: selectedConcept ? selectedConcept.name : prev.concept,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const enforceDueDateOrder =
      !editMode ||
      (formData.emission_date &&
        new Date(formData.emission_date) >= new Date(VALIDATION_START_DATE));

    if (!formData.emission_date) {
      newErrors.emission_date = true;
    }
    if (!formData.uuid_movement_type) {
      newErrors.uuid_movement_type = true;
    }
    if (!formData.amount || parseFloat(formData.amount) === 0) {
      newErrors.amount = true;
    }
    if (!formData.due_date) {
      newErrors.due_date = 'required';
    } else if (
      enforceDueDateOrder &&
      formData.emission_date &&
      new Date(formData.due_date) < new Date(formData.emission_date)
    ) {
      newErrors.due_date = 'before';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const bankAccountId = account?.uuid ?? movement?.uuid_bank_account ?? null;
      const movementData = {
        uuid_bank_account: bankAccountId,
        uuid_movement_type: formData.uuid_movement_type || null,
        uuid_movement_concept: formData.uuid_movement_concept || null,
        emission_date: new Date(formData.emission_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        amount: parseFloat(formData.amount),
        concept: formData.concept.trim() || null,
        voucher_number: formData.voucher_number.trim() || null,
        uuid_legal_entity: formData.uuid_legal_entity || null,
        is_reconciled: formData.is_reconciled,
        observations: formData.observations.trim() || null,
      };

      if (editMode) {
        await updateMovementMutation.mutateAsync({
          id: movement.uuid,
          data: movementData,
        });
      } else {
        await createMovementMutation.mutateAsync(movementData);
      }

      onClose();
    } catch (error) {
      console.error('Error saving movement:', error);
    }
  };

  const isLoading = createMovementMutation.isPending || updateMovementMutation.isPending;
  
  const dueDateHelperText =
    errors.due_date === 'before'
      ? 'La fecha de vencimiento no puede ser anterior a la fecha de emisión'
      : errors.due_date
      ? 'La fecha de vencimiento es obligatoria'
      : undefined;

  return (
    <DialogContainer open={open} onClose={onClose} size={isMobile ? 'fullScreen' : 'medium'}>
      <DialogTitle>{editMode ? 'Editar Movimiento' : 'Ingresar un movimiento'}</DialogTitle>

      <DialogBody sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {account && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <strong>Banco:</strong> {formatBankName(account.bank_name)}
              </Box>
              <Box>
                <strong>Cuenta:</strong> {account.account_number}
              </Box>
              <Box>
                <strong>Tipo:</strong> {formatAccountType(account.account_type)}
              </Box>
            </Box>
          </Box>
        )}

        {isMobile ? (
          <VerticalForm>
            <CustomAutoComplete
              label="Tipo de movimiento"
              name="uuid_movement_type"
              placeholder="Seleccionar tipo"
              value={formData.uuid_movement_type}
              onChange={handleInputChange}
              error={errors.uuid_movement_type}
              required
            >
              <MenuItem value="">Seleccionar tipo</MenuItem>
              {movementTypes
                .filter((type) => type.is_active !== false)
                .map((type) => (
                  <MenuItem key={type.uuid} value={type.uuid}>
                    {type.description}
                  </MenuItem>
                ))}
            </CustomAutoComplete>

            <CustomAutoComplete
              label="Concepto de movimiento"
              name="uuid_movement_concept"
              placeholder="Seleccionar concepto"
              value={formData.uuid_movement_concept}
              onChange={handleInputChange}
            >
              <MenuItem value="">Sin concepto</MenuItem>
              {movementConcepts
                .filter((concept) => concept.is_active !== false)
                .map((concept) => (
                  <MenuItem key={concept.uuid} value={concept.uuid}>
                    {concept.name}
                  </MenuItem>
                ))}
            </CustomAutoComplete>

            <CustomTextField
              label="Fecha de emisión"
              name="emission_date"
              type="date"
              value={formData.emission_date}
              onChange={handleInputChange}
              error={errors.emission_date}
              required
              shrinkLabel
            />

            <CustomTextField
              label="Fecha de vencimiento"
              name="due_date"
              type="date"
              value={formData.due_date}
              onChange={handleInputChange}
              error={Boolean(errors.due_date)}
              helperText={dueDateHelperText}
              required
              shrinkLabel
            />

            <CustomTextField
              label="Importe"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              error={errors.amount}
              required
              inputProps={{ step: 0.01 }}
            />

            <CustomTextField
              label="Concepto"
              name="concept"
              value={formData.concept}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
          </VerticalForm>
        ) : (
          <HorizontalForm sx={{ pt: 0 }} variant="twoRows">
            <Box>
              <CustomAutoComplete
                label="Tipo de movimiento"
                name="uuid_movement_type"
                placeholder="Seleccionar tipo"
                value={formData.uuid_movement_type}
                onChange={handleInputChange}
                error={errors.uuid_movement_type}
                required
                sx={{ maxWidth: '350px', mr: 1.5 }}
              >
                <MenuItem value="">Seleccionar tipo</MenuItem>
                {movementTypes
                  .filter((type) => type.is_active !== false)
                  .map((type) => (
                    <MenuItem key={type.uuid} value={type.uuid}>
                      {type.description}
                    </MenuItem>
                  ))}
              </CustomAutoComplete>

              <CustomTextField
                label="Num. comprob."
                name="voucher_number"
                value={formData.voucher_number}
                onChange={handleInputChange}
                sx={{ maxWidth: '350px', mr: 1.5  }}
              />

              <CustomAutoComplete
                label="Concepto de movimiento"
                name="uuid_movement_concept"
                placeholder="Seleccionar concepto"
                value={formData.uuid_movement_concept}
                onChange={handleInputChange}
                sx={{ maxWidth: '350px' }}
              >
                <MenuItem value="">Sin concepto</MenuItem>
                {movementConcepts
                  .filter((concept) => concept.is_active !== false)
                  .map((concept) => (
                    <MenuItem key={concept.uuid} value={concept.uuid}>
                      {concept.name}
                    </MenuItem>
                  ))}
              </CustomAutoComplete>
            </Box>

            <Box>
              <CustomTextField
                label="Fecha de emisión"
                name="emission_date"
                type="date"
                value={formData.emission_date}
                onChange={handleInputChange}
                error={errors.emission_date}
                required
                shrinkLabel
                sx={{ maxWidth: '350px', mr: 1.5 }}
              />

              <CustomTextField
                label="Fecha de vencimiento"
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleInputChange}
                error={Boolean(errors.due_date)}
                helperText={dueDateHelperText}
                required
                shrinkLabel
                sx={{ maxWidth: '350px', mr: 1.5 }}
              />

              <CustomTextField
                label="Importe"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                error={errors.amount}
                required
                inputProps={{ step: 1 }}
                sx={{ maxWidth: '350px' }}
              />
            </Box>

            <Box>
              <BeneficiarySelectWithCreate
                label="Beneficiario"
                name="uuid_legal_entity"
                value={formData.uuid_legal_entity}
                onChange={handleInputChange}
                legalEntities={legalEntities}
                placeholder="Sin beneficiario"
                containerSx={{ flex: 1 }}
              />
            </Box>
          </HorizontalForm>
        )}

        <Box sx={{ px: 3 }}>
          <CustomTextArea
            label="Observaciones"
            name="observations"
            value={formData.observations}
            onChange={handleInputChange}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleSave}
        saveText="Guardar"
        isLoading={isLoading}
      />
    </DialogContainer>
  );
};

export default MovementDialog;
